import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, useScroll, useTransform } from "framer-motion";
import { useBookingModal } from "../../context/BookingModalContext";
import { getImageUrl } from "../../services/api";
import defaultBlobImage from "../../assets/img/s1.jpg";
import "./PageAboutSection.scss";

// Two organic blob outlines (objectBoundingBox fractions, 0-1) that the SVG
// <animate> below morphs between continuously — this is what gives the
// smooth, rounded "liquid" motion instead of a faceted polygon.
const BLOB_SHAPE_A =
  "M 0.80923,0.5 C 0.81233,0.63874 0.73944,0.82918 0.62045,0.8707 C 0.50146,0.91222 0.30175,0.82778 0.21428,0.70759 C 0.12681,0.5874 0.10495,0.37587 0.18308,0.26975 C 0.26122,0.16363 0.47972,0.13095 0.60495,0.177 C 0.73018,0.22305 0.80613,0.36126 0.80923,0.5 Z";
const BLOB_SHAPE_B =
  "M 0.80021,0.5 C 0.80315,0.63412 0.73783,0.81329 0.61631,0.85796 C 0.49478,0.90263 0.26926,0.83374 0.19259,0.72335 C 0.11592,0.61295 0.15116,0.41318 0.23296,0.30599 C 0.31476,0.19879 0.48813,0.14855 0.60158,0.18736 C 0.71503,0.22616 0.79726,0.36588 0.80021,0.5 Z";
const BLOB_ANIMATE_VALUES = `${BLOB_SHAPE_A};${BLOB_SHAPE_B};${BLOB_SHAPE_A}`;

const PageAboutSection = ({ pageKey, fallbackHeading, fallbackParagraph }) => {
  const about = useSelector((state) => state.website.settings?.pageAboutSections?.[pageKey]);
  const { openModal } = useBookingModal();
  const blobRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: blobRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-18%", "18%"]);

  const heading = about?.heading || fallbackHeading;
  const paragraph = about?.paragraph || fallbackParagraph;
  const image = about?.image ? getImageUrl(about.image) : defaultBlobImage;

  if (!heading && !paragraph) return null;

  return (
    <section className="page-about">
      <div className="page-about__inner">
        <div className="page-about__text">
          <h2 className="page-about__heading">{heading}</h2>
          {paragraph && (
            <div className="page-about__paragraph">
              {paragraph.split("\n\n").map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          )}
          <div className="page-about__actions">
            <button
              type="button"
              className="page-about__btn page-about__btn--solid"
              onClick={() => setTimeout(openModal, 0)}
            >
              Book an Appointment
            </button>
            <Link to="/services" className="page-about__btn page-about__btn--outline">
              Our Services
            </Link>
            <Link to="/contact" className="page-about__btn page-about__btn--navy">
              Contact Us
            </Link>
          </div>
        </div>
        <div className="page-about__blob-wrap">
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <clipPath id="page-about-blob-clip" clipPathUnits="objectBoundingBox">
                <path d={BLOB_SHAPE_A}>
                  <animate
                    attributeName="d"
                    dur="6s"
                    repeatCount="indefinite"
                    values={BLOB_ANIMATE_VALUES}
                  />
                </path>
              </clipPath>
            </defs>
          </svg>
          <div className="page-about__blob" ref={blobRef}>
            <motion.img src={image} alt={heading} style={{ y }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PageAboutSection;
